import { Button, Form, FormInput, Dropdown } from '@praktio/common-ui';
import { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { cloneDeep, isEqual, sortBy } from 'lodash';
import classNames from 'classnames';

import { RootState } from '../../../ducks/store';
import {
  getUser,
  getCourseProgressDetails,
  updateCourseProgressDetails,
} from '../../../ducks/users';
import { BreadcrumbItem, Breadcrumbs } from '../../common/Breadcrumbs/Breadcrumbs';
import { RightPaneHeader } from '../../common/RightPane/RightPane';

import './UserCourseProgressDetails.scss';
import ResetProgressModal from '../modals/ResetProgressModal/ResetProgressModal';
import OverlaySpinner from '../../common/OverlaySpinner/OverlaySpinner';
import FormSection from '../../common/FormSection/FormSection';
import { getLocalDateTime } from '../../../utils/functions';
import { Roles } from '../../../utils';

interface IProps {
  closeButtonAction: () => void;
}

interface IData extends IKey {
  userExpirationOverride?: Date | null;
  courseCompletedOverride?: { completedOverride: Date | null; isDelete: boolean };
  moduleCompletedOverrides?: { moduleId: string; completedOverride: Date; isDelete: boolean }[];
}

const UserCourseProgressDetails: FC<IProps> = ({ closeButtonAction }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = searchParams.get('userId') as string;
  const courseId = searchParams.get('courseId') as string;
  const tab = searchParams.get('tab') as string;

  const { user, assignedCourses, course, courseAssignment, immediatelyReleasedCourseById } =
    useSelector((state: RootState) => {
      const { current: user, courseDetails } = state.users;
      const { assignedCourses, course, courseAssignment } = courseDetails || {};
      const { list: teams } = state.teams;

      const immediatelyReleasedCourseById: IKey = {};
      for (const role of user?.roles || []) {
        if (role.role === Roles.Learner && role.teamId) {
          const team = teams?.find((t) => t._id === role.teamId);
          if (!team?.enableTimeRelease) {
            team?.courses?.forEach((cId) => (immediatelyReleasedCourseById[cId as string] = true));
          }
        }
      }

      return {
        assignedCourses: sortBy<ICourse>(assignedCourses, 'title') || ([] as ICourse[]),
        course: course || ({} as ICourse),
        courseAssignment,
        user,
        teams,
        immediatelyReleasedCourseById,
      };
    });
  const { title, completed, progress } = course;

  const originalData = useRef<IData>({
    userExpirationOverride: null,
    moduleCompletedOverrides: [],
  });
  const [data, setData] = useState<IData>(originalData.current);
  const [savedChanges, setSavedChanges] = useState(false);
  const [showResetProgressModal, setShowResetProgressModal] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);

  useEffect(() => {
    dispatch(getUser(userId));
    dispatch(getCourseProgressDetails(userId, courseId));
  }, [dispatch, courseId, userId]);

  useEffect(() => {
    const initialData: IData = {
      userExpirationOverride: courseAssignment?.userExpirationOverride || null,
      moduleCompletedOverrides: [],
    };

    if (course.completedOverride) {
      initialData.courseCompletedOverride = {
        completedOverride: course.completedOverride,
        isDelete: false,
      };
    }

    for (const module of course.modules || []) {
      if (module.completedOverride) {
        initialData.moduleCompletedOverrides?.push({
          moduleId: module._id as string,
          completedOverride: module.completedOverride,
          isDelete: false,
        });
      }
    }

    originalData.current = initialData;
    setData(initialData);
  }, [user, course, courseAssignment, courseId]);

  const headerText = `${user?.firstName} ${user?.lastName}`;

  const { completedOverride, isDelete } = data?.courseCompletedOverride || {};
  const completedValue =
    completedOverride && !isDelete
      ? completedOverride
      : completed !== course.completedOverride
      ? completed
      : undefined;

  const courseProgressPendingSave = isDelete;
  const progressValue = courseProgressPendingSave
    ? 'Pending Save'
    : `${(progress || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`;

  const hasChanges = !isEqual(originalData.current, data);

  const assignmentOptions = assignedCourses.map((ac) => ({
    id: `courseId=${ac._id}`,
    label: ac.title as string,
  }));

  const courseAssigned = courseAssignment?.assigned;
  const hasTimeReleasedModules =
    !!course?.modules?.find((m) => !!m.timeReleasePeriod) && courseAssigned;

  return (
    <div className="UserCourseProgressDetails">
      <RightPaneHeader
        headerText={headerText}
        savedChanges={savedChanges}
        unsavedChanges={hasChanges}
        closeButtonAction={closeButtonAction}
        mainButton={
          <Button
            disabled={!hasChanges}
            onClick={() => {}}
            form="user-assignment-details-form"
            type="submit"
            btnStyle="primary"
          >
            SAVE
          </Button>
        }
      />
      <Breadcrumbs>
        <BreadcrumbItem
          key={`user-breadcrumb`}
          target={`${location.pathname}?userId=${userId}${tab ? `&tab=${tab}` : ''}`}
        >
          User Settings
        </BreadcrumbItem>
        <BreadcrumbItem key={`user-assignment-breadcrumb`} target="" active>
          Course Assignment Details
        </BreadcrumbItem>
      </Breadcrumbs>
      <Form
        formId="user-assignment-details-form"
        className="text-left"
        onSubmit={async (event: any) => {
          event.preventDefault();
          if (hasChanges) {
            // Pass only the things that are getting updated
            const updates = cloneDeep(data);
            for (const key of Object.keys(updates)) {
              if (key !== 'moduleCompletedOverrides') {
                if (isEqual(updates[key], originalData.current[key])) {
                  delete updates[key];
                } else if (updates[key] && key === 'userExpirationOverride') {
                  let data = updates[key];

                  // If value doesn't contain a Z, then it is in
                  // the datetime-local format, so wrap in a Date
                  // to convert to ISO/UTC
                  if (
                    data &&
                    typeof data === 'string' &&
                    (data as string).includes &&
                    !(data as string).includes('Z')
                  ) {
                    data = new Date(data);
                  }

                  updates.userExpirationOverride = data;
                }
              } else {
                const overrides = cloneDeep(updates.moduleCompletedOverrides);
                for (const override of updates.moduleCompletedOverrides || []) {
                  const originalOverride = originalData.current.moduleCompletedOverrides?.find(
                    (m) => m.moduleId === override.moduleId,
                  );
                  if (isEqual(override, originalOverride)) {
                    const index = overrides?.findIndex((m) => m.moduleId === override.moduleId);
                    if (index || index === 0) {
                      overrides?.splice(index, 1);
                    }
                  }
                }

                updates.moduleCompletedOverrides = overrides;
              }
            }

            const saveSuccess = (await dispatch(
              updateCourseProgressDetails({ userId, courseId, details: updates }),
            )) as any;

            if (saveSuccess) {
              setSavedChanges(true);
            }
          }
        }}
      >
        <div>
          <Dropdown
            id="assignment-dropdown"
            options={assignmentOptions}
            value={assignmentOptions.find((o) => o.id === `courseId=${courseId}`)}
            onSelect={(id) =>
              navigate(`${location.pathname}?userId=${userId}${tab ? `&tab=${tab}` : ''}&${id}`)
            }
          />
        </div>
        <FormSection subheader={title}>
          <FormInput
            label="User Manual Expiration Override"
            type="datetime-local"
            value={getLocalDateTime(data.userExpirationOverride)}
            onChange={(e: any) => {
              const value = e.target.value;
              setData({ ...data, userExpirationOverride: value });
            }}
            lastInSection
          />
        </FormSection>
        <FormSection subheader="Progress">
          <div className="progress-buttons">
            <Button
              disabled={(!!completed && !completedOverride) || courseProgressPendingSave}
              onClick={() => {
                const originalExistingOverride = originalData.current.courseCompletedOverride;
                let courseCompletedOverride = cloneDeep(data.courseCompletedOverride);

                if (!courseCompletedOverride) {
                  courseCompletedOverride = {
                    completedOverride: new Date(),
                    isDelete: false,
                  };
                }
                // If the original data didn't have the override,
                // remove it completely so the hasChanges check can run as expected
                else if (!originalExistingOverride) {
                  courseCompletedOverride = undefined;
                } else {
                  courseCompletedOverride.isDelete = true;
                }

                setData({
                  ...data,
                  courseCompletedOverride,
                });
              }}
              className="secondary"
            >
              {completedOverride && !isDelete ? 'Clear Override' : 'Set Course as Complete'}
            </Button>
            <Button className="secondary" onClick={() => setShowResetProgressModal(true)}>
              RESET PROGRESS
            </Button>
          </div>
          <div className="grid-wrap">
            <h4>Course Completed</h4>
            <div>
              {completedValue ? new Date(completedValue).toLocaleDateString() : progressValue}
            </div>
          </div>
          <h4>Modules</h4>
          <div className={classNames('module-grid', { four: hasTimeReleasedModules })}>
            <div className="grid-header">Title</div>
            <div className="grid-header">Completed</div>
            {!!hasTimeReleasedModules && <div className="grid-header">Released</div>}
            <div></div>
            {course.modules?.map((m) => {
              const { _id, title, completed, progress, isRetake, timeReleasePeriod } = m;
              const courseSetToImmediatelyReleased = immediatelyReleasedCourseById[courseId];
              const moduleCompletedOverride = data.moduleCompletedOverrides?.find(
                (m) => m.moduleId === _id,
              );
              const { isDelete, completedOverride } = moduleCompletedOverride || {};
              const moduleProgressPendingSave = isDelete;
              const completedValue =
                completedOverride && !isDelete
                  ? completedOverride
                  : completed !== completedOverride
                  ? completed
                  : undefined;
              const progressValue = moduleProgressPendingSave
                ? 'Pending Save'
                : `${(progress || 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}%`;

              const assignedDate = courseAssigned ? new Date(courseAssigned) : undefined;
              return (
                <>
                  <div>{title}</div>
                  <div>
                    {completedValue ? new Date(completedValue).toLocaleDateString() : progressValue}
                  </div>
                  {!!hasTimeReleasedModules && !!assignedDate && (
                    <div>
                      {(!courseSetToImmediatelyReleased && timeReleasePeriod
                        ? new Date(assignedDate.setDate(assignedDate.getDate() + timeReleasePeriod))
                        : assignedDate
                      ).toLocaleDateString()}
                    </div>
                  )}
                  <div>
                    <Button
                      disabled={
                        isRetake || (!!completed && !completedOverride) || moduleProgressPendingSave
                      }
                      onClick={() => {
                        const moduleCompletedOverrides = cloneDeep(
                          data.moduleCompletedOverrides || [],
                        );

                        const originalExistingOverride =
                          originalData.current.moduleCompletedOverrides?.find(
                            (m) => m.moduleId === _id,
                          );

                        const existingIndex = moduleCompletedOverrides.findIndex(
                          (m) => m.moduleId === _id,
                        );

                        if (existingIndex < 0) {
                          moduleCompletedOverrides.push({
                            moduleId: _id as string,
                            completedOverride: new Date(),
                            isDelete: false,
                          });
                        }
                        // If the original data didn't have the override,
                        // remove it completely so the hasChanges check can run as expected
                        else if (!originalExistingOverride) {
                          moduleCompletedOverrides.splice(existingIndex, 1);
                        }
                        // Otherwise, the module override existed, so we want
                        // to remove it at the API
                        else {
                          const existingOverride = moduleCompletedOverrides[existingIndex];
                          existingOverride.isDelete = true;
                        }

                        setData({ ...data, moduleCompletedOverrides });
                      }}
                      tooltip={
                        isRetake
                          ? 'This is unavailable because the Learner has started retaking this Module.'
                          : undefined
                      }
                      className="secondary"
                    >
                      {completedOverride && !isDelete ? 'Clear Override' : 'Set as Complete'}
                    </Button>
                  </div>
                </>
              );
            })}
          </div>
        </FormSection>
      </Form>
      <ResetProgressModal
        show={showResetProgressModal}
        onHide={() => setShowResetProgressModal(false)}
        userId={userId}
        courseId={courseId}
        email={user?.email}
        setShowOverlay={setShowOverlay}
      />
      {!!showOverlay && (
        <div className="overlay-wrapper">
          <OverlaySpinner />
        </div>
      )}
    </div>
  );
};

export default UserCourseProgressDetails;
